import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Check, FolderOpen } from 'lucide-react';
import { useLocation, useRoute } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { getProgram, addProgram, updateProgram, Timestamp, deleteField } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// Import step components
import Step1Categorisation from '@/components/add-program/step1-categorisation';
import Step2Description from '@/components/add-program/step2-description';
import Step3BuildComponents from '@/components/add-program/step3-build-components';
import Step5AdditionalVideos from '@/components/add-program/step5-additional-videos';
import Step6SaveProgram from '@/components/add-program/step6-save-program';

// Component type interfaces
export interface VideoTask {
  contentId: string;
  durationMinutes: number;
  externalId: string;
  id: string;
  taskNumber: number;
  title: string;
  thumbnailUrl?: string;
}

export interface DynamicTask {
  id?: string; // Unique identifier for stable React keys
  taskNumber: number;
  taskMessage: string;
  inputType: string;
  thumbnailUrl?: string;
}

export interface BaseComponent {
  componentType: 'videoSeries' | 'repeatingTask' | 'steps' | 'dynamicTask';
  componentOrder: number;
  startOnDay: number;
}

export interface VideoSeriesComponent extends BaseComponent {
  componentType: 'videoSeries';
  tasks: VideoTask[];
}

export interface RepeatingTaskComponent extends BaseComponent {
  componentType: 'repeatingTask';
  tasks: DynamicTask[];
}

export interface StepsComponent extends BaseComponent {
  componentType: 'steps';
  target: number;
}

export interface DynamicTaskComponent extends BaseComponent {
  componentType: 'dynamicTask';
  tasks: DynamicTask[];
}

export type ProgramComponent = 
  | VideoSeriesComponent 
  | RepeatingTaskComponent 
  | StepsComponent 
  | DynamicTaskComponent;

// A component as stored in Firestore. Older programs may lack startOnDay, and
// legacy repeatingTask components hold a single task inline instead of `tasks`.
type StoredProgramComponent =
  | (Omit<VideoSeriesComponent, 'startOnDay'> & { startOnDay?: number })
  | (Omit<RepeatingTaskComponent, 'startOnDay' | 'tasks'> & {
      startOnDay?: number;
      tasks?: DynamicTask[];
      taskMessage?: string;
      inputType?: string;
    })
  | (Omit<StepsComponent, 'startOnDay'> & { startOnDay?: number })
  | (Omit<DynamicTaskComponent, 'startOnDay'> & { startOnDay?: number });

export interface ProgramData {
  // Step 1 - Categorisation
  programType: 'videoSeries' | 'bundle' | '';
  module: 'general' | 'mind' | 'body' | '';
  contentLibrary: string;
  contentLibraryName: string;
  fixedLength: number | null;

  // Step 2 - Description
  name: string;
  subtitle: string;
  notesGeneral: string;
  notesWho: string;
  notesAdvice: string;

  // Step 3 - Components (new structure)
  components: ProgramComponent[];

  // Step 4 - Additional Settings
  instructionContentId: string;
  instructionExternalId: string;
  conclusionContentId: string;
  conclusionExternalId: string;
  bannerUrl: string;

  // Default fields
  enabled: boolean;

  // Legacy fields (for backward compatibility during migration)
  selectedContent?: any[];
  tasks?: any[];
}

const TOTAL_STEPS = 6;

// Calculate fixed length from components (endDay = startOnDay + numberOfTasks - 1 for each component)
const calculateFixedLength = (components: ProgramComponent[]): number | null => {
  if (!components || components.length === 0) return null;
  
  let maxEndDay = 0;
  
  for (const component of components) {
    const startDay = component.startOnDay || 1;
    let taskCount = 0;
    
    if (component.componentType === 'videoSeries' && 'tasks' in component) {
      taskCount = component.tasks.length;
    } else if (component.componentType === 'repeatingTask' && 'tasks' in component) {
      taskCount = component.tasks.length || 1;
    } else if (component.componentType === 'steps') {
      taskCount = 1;
    } else if (component.componentType === 'dynamicTask' && 'tasks' in component) {
      taskCount = component.tasks.length;
    }
    
    if (taskCount > 0) {
      const endDay = startDay + taskCount - 1;
      maxEndDay = Math.max(maxEndDay, endDay);
    }
  }
  
  return maxEndDay > 0 ? maxEndDay : null;
};

export default function AddProgram() {
  const [currentStep, setCurrentStep] = useState(1);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [match, params] = useRoute('/programs/edit/:id');
  const programId = params?.id;
  const isEditMode = !!programId;
  const [loading, setLoading] = useState(isEditMode);
  
  const [programData, setProgramData] = useState<ProgramData>({
    programType: '',
    module: '',
    contentLibrary: '',
    contentLibraryName: '',
    fixedLength: null,
    name: '',
    subtitle: '',
    notesGeneral: '',
    notesWho: '',
    notesAdvice: '',
    components: [],
    instructionContentId: '',
    instructionExternalId: '',
    conclusionContentId: '',
    conclusionExternalId: '',
    bannerUrl: '',
    enabled: true,
  });

  const [canProceed, setCanProceed] = useState(false);

  useEffect(() => {
    const loadProgram = async () => {
      if (!programId) return;
      
      try {
        setLoading(true);
        const data = await getProgram(programId);
        
        if (data) {
          logger.log('📋 Loaded program for editing:', data);
          
          // Handle backward compatibility: convert old tasks structure to new components structure
          // Also normalize dynamic task IDs
          let components: ProgramComponent[] = [];
          let needsIdNormalization = false;
          
          if (Array.isArray(data.components) && data.components.length > 0) {
            // New structure: program has components array
            // Normalize dynamic tasks to ensure they have stable IDs
            // Also ensure all components have startOnDay field
            components = data.components.map((comp: StoredProgramComponent) => {
              // Ensure startOnDay defaults to 1 if missing
              const normalizedComp = {
                ...comp,
                startOnDay: comp.startOnDay ?? 1
              };
              
              if (comp.componentType === 'repeatingTask') {
                if (Array.isArray(comp.tasks)) {
                  const hasTasksWithoutIds = comp.tasks.some((t: DynamicTask) => !t.id);
                  if (hasTasksWithoutIds) {
                    needsIdNormalization = true;
                  }
                  return {
                    ...normalizedComp,
                    tasks: comp.tasks.map((t: DynamicTask) => ({
                      ...t,
                      id: t.id || crypto.randomUUID()
                    }))
                  } as RepeatingTaskComponent;
                } else {
                  return {
                    ...normalizedComp,
                    tasks: [{
                      id: crypto.randomUUID(),
                      taskNumber: 1,
                      taskMessage: comp.taskMessage || '',
                      inputType: comp.inputType || 'checkBox'
                    }]
                  } as RepeatingTaskComponent;
                }
              }
              if (comp.componentType === 'dynamicTask') {
                const hasTasksWithoutIds = comp.tasks.some((t: DynamicTask) => !t.id);
                if (hasTasksWithoutIds) {
                  needsIdNormalization = true;
                }
                return {
                  ...normalizedComp,
                  tasks: comp.tasks.map((t: DynamicTask) => ({
                    ...t,
                    id: t.id || crypto.randomUUID()
                  }))
                } as DynamicTaskComponent;
              }
              return normalizedComp;
            });
            logger.log('📋 Loaded and normalized program components:', components.length);
          } else if (Array.isArray(data.tasks) && data.tasks.length > 0) {
            // Old structure: convert tasks array to single videoSeries component
            const videoSeriesComponent: VideoSeriesComponent = {
              componentType: 'videoSeries',
              componentOrder: 1,
              startOnDay: 1,
              tasks: data.tasks
            };
            components = [videoSeriesComponent];
            logger.log('📋 Converted old tasks structure to components:', components);
          }

          // If we generated new IDs for legacy tasks, persist them to Firestore immediately
          if (needsIdNormalization && programId) {
            logger.log('📋 Persisting normalized task IDs to Firestore');
            try {
              await updateProgram(programId, { components });
            } catch (error) {
              logger.error('Failed to persist normalized IDs:', error);
              // Continue anyway - IDs are still in memory
            }
          }
          
          setProgramData({
            programType: data.programType || '',
            module: data.module || '',
            contentLibrary: data.contentLibrary || '',
            contentLibraryName: data.contentLibraryName || '',
            fixedLength: data.fixedLength ?? null,
            name: data.name || '',
            subtitle: data.subtitle || '',
            notesGeneral: data.notesGeneral || '',
            notesWho: data.notesWho || '',
            notesAdvice: data.notesAdvice || '',
            components,
            instructionContentId: data.instructionContentId || '',
            instructionExternalId: data.instructionExternalId || '',
            conclusionContentId: data.conclusionContentId || '',
            conclusionExternalId: data.conclusionExternalId || '',
            bannerUrl: data.bannerUrl || '',
            enabled: data.enabled !== undefined ? data.enabled : true,
          });
          
          logger.log('📋 Loaded program - components count:', components.length);
        } else {
          toast({
            title: "Error",
            description: "Program not found",
            variant: "destructive",
          });
          navigate('/programs');
        }
      } catch (error) {
        logger.error('Error loading program:', error);
        toast({
          title: "Error",
          description: "Failed to load program",
          variant: "destructive",
        });
        navigate('/programs');
      } finally {
        setLoading(false);
      }
    };

    loadProgram();
  }, [programId, navigate, toast]);

  const stepTitles = [
    'Program Categorisation',
    'Program Description',
    'Build Components',
    'Additional Settings',
    'Review',
    'Save Program'
  ];

  const stepDescriptions = [
    'Choose the type, module, and content library for your program',
    'Add name and description details for your program',
    'Add and configure the components that make up this program',
    'Upload banner image and choose optional instruction and conclusion videos',
    'Review your program configuration before saving',
    'Save your program'
  ];

  const updateProgramData = (updates: Partial<ProgramData>) => {
    setProgramData(prev => ({ ...prev, ...updates }));
  };

  // Keep fixedLength in sync with components when the switch is ON
  // This runs whenever components change, regardless of which step is active
  useEffect(() => {
    // Only recalculate if fixedLength is not null (switch is ON)
    if (programData.fixedLength !== null && programData.components.length > 0) {
      const newLength = calculateFixedLength(programData.components);
      // Only update if we have a valid positive calculation and it's different
      if (newLength !== null && newLength > 0 && newLength !== programData.fixedLength) {
        setProgramData(prev => ({ ...prev, fixedLength: newLength }));
      }
    }
  }, [programData.components, programData.fixedLength]);

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!programData.module && !!programData.contentLibrary;
      case 2:
        return !!programData.name && !!programData.notesGeneral;
      case 3:
        return programData.components && programData.components.length > 0;
      case 4:
        return true; // Optional step
      case 5:
        return true; // Review step
      case 6:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const saveProgram = async () => {
    try {
      // Recalculate fixedLength before saving if fixed length is enabled
      // (fixedLength !== null means the switch is ON)
      let finalFixedLength = programData.fixedLength;
      if (finalFixedLength !== null) {
        // Recalculate based on current components
        const recalculated = calculateFixedLength(programData.components);
        // Only use recalculated value if it's valid (positive number)
        // Otherwise retain the prior stored value
        if (recalculated !== null && recalculated > 0) {
          finalFixedLength = recalculated;
        }
      }
      
      // Prepare program data with only the fields we want to store
      const programToSave: any = {
        programType: programData.programType,
        module: programData.module,
        contentLibrary: programData.contentLibrary,
        fixedLength: finalFixedLength, // null if not fixed, calculated number if fixed
        name: programData.name,
        subtitle: programData.subtitle || '',
        notesGeneral: programData.notesGeneral,
        notesWho: programData.notesWho,
        notesAdvice: programData.notesAdvice,
        components: programData.components,
        instructionContentId: programData.instructionContentId,
        instructionExternalId: programData.instructionExternalId,
        conclusionContentId: programData.conclusionContentId,
        conclusionExternalId: programData.conclusionExternalId,
        bannerUrl: programData.bannerUrl,
        enabled: programData.enabled,
        modified: Timestamp.now(),
      };

      // Clean empty string fields (but keep null for fixedLength)
      Object.keys(programToSave).forEach(key => {
        if (programToSave[key] === '' || programToSave[key] === undefined) {
          delete programToSave[key];
        }
      });

      if (isEditMode && programId) {
        // Update existing program - explicitly delete old fields
        await updateProgram(programId, {
          ...programToSave,
          // Explicitly remove legacy fields
          tasks: deleteField(),
          selectedContent: deleteField()
        });
        toast({
          title: "Program Updated",
          description: "Your program has been successfully updated.",
        });
      } else {
        // Create new program
        await addProgram({
          ...programToSave,
          created: Timestamp.now(),
        });
        toast({
          title: "Program Created",
          description: "Your program has been successfully created.",
        });
      }

      navigate('/programs');
    } catch (error) {
      logger.error('Error saving program:', error);
      toast({
        title: "Error",
        description: `There was an error ${isEditMode ? 'updating' : 'creating'} your program. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Categorisation
            data={programData}
            updateData={updateProgramData}
            onValidationChange={setCanProceed}
          />
        );
      case 2:
        return (
          <Step2Description
            data={programData}
            updateData={updateProgramData}
            onValidationChange={setCanProceed}
          />
        );
      case 3:
        return (
          <Step3BuildComponents
            data={programData}
            updateData={updateProgramData}
            onValidationChange={setCanProceed}
          />
        );
      case 4:
        return (
          <Step5AdditionalVideos
            data={programData}
            updateData={updateProgramData}
            onValidationChange={setCanProceed}
          />
        );
      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Review Program</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Program Name</p>
                  <p className="text-base">{programData.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Module</p>
                  <p className="text-base capitalize">{programData.module}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Components</p>
                  <p className="text-base">{programData.components?.length || 0} component(s)</p>
                </div>
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <Step6SaveProgram
            data={programData}
            onSave={saveProgram}
          />
        );
      default:
        return <div>Invalid step</div>;
    }
  };

  // Update canProceed when step changes
  useEffect(() => {
    setCanProceed(validateStep(currentStep));
  }, [currentStep, programData]);

  const progressPercentage = (currentStep / TOTAL_STEPS) * 100;

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading program...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/programs')}
              data-testid="button-back-to-programs"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Programs
            </Button>
          </div>
          
          <div className="flex items-center space-x-3 mb-4">
            <FolderOpen className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-slate-900">
              {isEditMode ? 'Edit Program' : 'Add New Program'}
            </h1>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-slate-600">
              <span>Step {currentStep} of {TOTAL_STEPS}</span>
              <span>{Math.round(progressPercentage)}% Complete</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mb-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            data-testid="button-previous-step"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {currentStep < TOTAL_STEPS ? (
            <Button
              onClick={nextStep}
              disabled={!canProceed}
              data-testid="button-next-step"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={saveProgram}
              className="bg-green-600 hover:bg-green-700"
              data-testid="button-save-program"
            >
              <Check className="w-4 h-4 mr-2" />
              {isEditMode ? 'Update Program' : 'Save Program'}
            </Button>
          )}
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <span className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
                {currentStep}
              </span>
              <span>{stepTitles[currentStep - 1]}</span>
            </CardTitle>
            <CardDescription>
              {stepDescriptions[currentStep - 1]}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}